import { VideoWithDocId } from '@yoimo/client-sdk/videos';

import { PageErrorDetails } from '@core/interfaces/types';
import {
  VideoAccessCheckFailureReasons as FailureReasons,
  VideoAccessCheckSuccessResult,
  VideoAccessCheckFailureResult,
} from '@core/services';

export type VideoAccessStatus =
  | VideoPageAccessCheckSuccess
  | VideoPageAccessCheckFailure;

export type VideoErrorState = {
  reason: AccessDeniedReason;
  details: PageErrorDetails;
};

type VideoPageAccessCheckSuccess = VideoAccessCheckSuccessResult & {
  /** Requested video. Includes `videoLocation` */
  video: VideoWithDocId;
};
type VideoPageAccessCheckFailure = VideoAccessCheckFailureResult & {
  isSubscriptionPaused: boolean;
};

type AccessDeniedReason = Exclude<
  FailureReasons,
  'SUBSCRIPTION_PAUSED' | 'NO_ACCESS'
>;

export function startsAfter(
  baseline: VideoWithDocId,
  video: VideoWithDocId
): boolean {
  return [video.startTime, video.uploadTime].some(
    (date) =>
      date &&
      [baseline.startTime, baseline.uploadTime].some(
        (vDate) => vDate && vDate?.getTime() < date.getTime()
      )
  );
}

export function startsBefore(
  baseline: VideoWithDocId,
  video: VideoWithDocId
): boolean {
  return [video.endTime, video.uploadTime].some(
    (date) =>
      date &&
      [baseline.endTime, baseline.uploadTime].some(
        (vDate) => vDate && vDate?.getTime() > date.getTime()
      )
  );
}

export function getVideoFilterPredicate(
  ignoreVideoId: string
): (video: VideoWithDocId) => boolean {
  return (video: VideoWithDocId) => video.docId !== ignoreVideoId;
}

export const VIDEO_ERROR_STATES: Record<AccessDeniedReason, PageErrorDetails> =
  {
    CONTENT_GONE: {
      title: $localize`:@@videoErrorContentGoneTitle:Video Unavailable`,
      description: $localize`:@@videoErrorContentGoneDescription:An error occurred during video loading. Please try again later.`,
      icon: 'novideo',
    },
    GEO_RESTRICTION: {
      title: $localize`:@@videoErrorGeoBlockedTitle:Sorry, the video is not available in your country`,
      icon: 'globe',
    },
    NEEDS_ACTION: {
      actionLabel: $localize`:@@videoErrorInvalidSubscriptionUpdateLabel:Update subscription`,
      description: $localize`:@@videoErrorInvalidSubscriptionDescription:We have detected an issue with your subscription. Please update it to continue watching`,
      icon: 'card',
      title: $localize`:@@videoErrorSubscriptionInactiveTitle:Subscription is inactive`,
    },
    ACCESS_FAILED: {
      description: $localize`:@@videoErrorAccessFailedDescription:An error occurred while trying to contact the server. Please check your internet connection and try to reload the page`,
      icon: 'warning_solid',
      title: $localize`:@@videoErrorAccessFailedTitle:Contacting server has failed`,
    },
  };
